'use client';

import {
    createContext,
    useCallback,
    useContext,
    useRef,
    type ComponentPropsWithRef,
    type ReactElement,
    type ReactNode,
} from 'react';
import { CopyIcon } from '@/shared-ui/icons/copy';
import { Icon } from '@/shared-ui/atoms/icon';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

type RootProps = Omit<ComponentPropsWithRef<'div'>, 'children'> & {
    children: ReactNode;
    /** When set, applied as inline `background-color` (e.g. `#D9948F`). Otherwise the default token background is used. */
    backgroundColorHex?: string;
    /** Minimum height of the strip in pixels (inline `min-height`). Omit for no minimum beyond content + padding. */
    minHeightPx?: number;
};

type ContentProps = Omit<ComponentPropsWithRef<'div'>, 'children'> & {
    children: ReactNode;
    /** When set, applied as inline `color` to the banner content wrapper (e.g. `#111827`). */
    textColorHex?: string;
};

type CopyButtonProps = Omit<ComponentPropsWithRef<typeof Button>, 'children' | 'type' | 'size' | 'variant'> & {
    /** Accessible name for the copy control (e.g. "Copy promo code"). */
    copyButtonLabel?: string;
    /** Optional explicit promo code to copy. When set, this is copied instead of banner text. */
    promoCode?: string;
};

type IconWrapperProps = Omit<ComponentPropsWithRef<'div'>, 'children'> & {
    children?: ReactNode;
};

type FullWidthBannerContextValue = {
    contentRef: React.RefObject<HTMLDivElement | null>;
};

const FullWidthBannerContext = createContext<FullWidthBannerContextValue | null>(null);

async function writeTextToClipboard(text: string): Promise<void> {
    if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return;
    }
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
}

const Root = ({
    children,
    backgroundColorHex,
    minHeightPx,
    className,
    ref,
    style,
    ...props
}: RootProps): ReactElement => {
    const contentRef = useRef<HTMLDivElement>(null);

    const minHeightStyle =
        typeof minHeightPx === 'number' && Number.isFinite(minHeightPx)
            ? { minHeight: `${Math.max(0, Math.round(minHeightPx))}px` }
            : undefined;

    return (
        <FullWidthBannerContext.Provider value={{ contentRef }}>
            <div
                ref={ref}
                className={cn(
                    "relative flex w-full flex-col justify-center py-2 text-center text-base text-foreground px-4 [&:has([data-slot='full-width-banner-copy-button'])_[data-slot='full-width-banner-content']]:px-12 [&:has([data-slot='full-width-banner-icon-wrapper'])_[data-slot='full-width-banner-content']]:px-12",
                    !backgroundColorHex && 'bg-muted',
                    className
                )}
                style={{
                    ...style,
                    ...(backgroundColorHex ? { backgroundColor: backgroundColorHex } : {}),
                    ...minHeightStyle,
                }}
                data-slot="full-width-banner"
                {...props}>
                {children}
            </div>
        </FullWidthBannerContext.Provider>
    );
};
Root.displayName = 'FullWidthBanner.Root';

const Content = ({ children, textColorHex, className, ref, ...props }: ContentProps): ReactElement => {
    const context = useContext(FullWidthBannerContext);

    if (!context) {
        throw new Error('FullWidthBanner.Content must be used within FullWidthBanner.Root.');
    }

    const setContentRef = useCallback(
        (node: HTMLDivElement | null) => {
            context.contentRef.current = node;
            if (typeof ref === 'function') {
                ref(node);
                return;
            }
            if (ref) {
                ref.current = node;
            }
        },
        [context.contentRef, ref]
    );

    return (
        <div
            ref={setContentRef}
            data-slot="full-width-banner-content"
            className={cn('mx-auto max-w-5xl', className)}
            style={textColorHex ? { color: textColorHex } : undefined}
            {...props}>
            {children}
        </div>
    );
};
Content.displayName = 'FullWidthBanner.Content';

const CopyButton = ({
    copyButtonLabel = 'Copy banner text to clipboard',
    promoCode,
    className,
    onClick,
    ...props
}: CopyButtonProps) => {
    const context = useContext(FullWidthBannerContext);

    if (!context) {
        throw new Error('FullWidthBanner.CopyButton must be used within FullWidthBanner.Root.');
    }

    const handleCopy = useCallback(async () => {
        const text =
            promoCode !== undefined ? promoCode.trim() : (context.contentRef.current?.textContent?.trim() ?? '');
        if (!text) return;
        await writeTextToClipboard(text);
    }, [context.contentRef, promoCode]);

    return (
        <Button
            data-slot="full-width-banner-copy-button"
            type="button"
            variant="ghost"
            size="icon"
            className={cn(
                'absolute right-0 top-1/2 size-9 shrink-0 -translate-y-1/2 cursor-pointer text-foreground hover:bg-transparent hover:text-foreground',
                className
            )}
            aria-label={copyButtonLabel}
            onClick={(event) => {
                onClick?.(event);
                if (!event.defaultPrevented) {
                    void handleCopy();
                }
            }}
            {...props}>
            <Icon icon={CopyIcon} className="size-4" aria-hidden />
        </Button>
    );
};
CopyButton.displayName = 'FullWidthBanner.CopyButton';

const IconWrapper = ({ children, className, ...props }: IconWrapperProps) => {
    return (
        <div
            data-slot="full-width-banner-icon-wrapper"
            className={cn('absolute right-0 top-1/2 -translate-y-1/2 leading-3', className)}
            {...props}>
            {children}
        </div>
    );
};
IconWrapper.displayName = 'FullWidthBanner.IconWrapper';

const FullWidthBanner = {
    Root,
    Content,
    CopyButton,
    IconWrapper,
} as const;

export { FullWidthBanner };
